import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Trophy } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { createDemoRequestPayload, createEmptyDemoRequest } from "@/lib/pet-vet/publicForms";
import { submitPetVetForm } from "./publicFormApi";

export default function PetVetDemoForm() {
  const { t } = useTranslation("petVet");
  const [form, setForm] = useState<Record<string, string | boolean>>(createEmptyDemoRequest);
  const [status, setStatus] = useState("idle");

  const set = (key: string, value: string | boolean) => {
    setForm((old) => ({ ...old, [key]: value }));
  };

  const maskPhone = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    return digits.length > 6
      ? `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
      : digits;
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setStatus("sending");

    try {
      await submitPetVetForm("demo", createDemoRequestPayload(form));
      setStatus("success");
    } catch {
      setStatus("error");
    } finally {
      setForm(createEmptyDemoRequest());
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-orange-50 via-background to-sky-50 px-4 py-8">
      <Card className="mx-auto max-w-xl border-border/70 bg-card/95 shadow-xl shadow-primary/10 backdrop-blur">
        <CardHeader>
          <img
            src="/assets/images/logo.svg"
            alt="Akuna"
            className="mb-5 h-10 w-auto object-contain object-left"
          />
          <CardTitle className="text-3xl">{t("demoTitle")}</CardTitle>
          <p className="text-muted-foreground">{t("demoSubtitle")}</p>
        </CardHeader>

        <CardContent className="[&_input::placeholder]:text-muted-foreground/50 [&_textarea::placeholder]:text-muted-foreground/60">
          <form onSubmit={submit} className="space-y-5">
            {(["name", "clinic", "role", "email"] as const).map((key) => (
              <label key={key} className="block text-sm font-medium">
                {t(key)}
                <input
                  placeholder={t(`${key}Placeholder`)}
                  className="mt-2 h-11 w-full rounded-md border bg-background px-3"
                  value={(form[key] as string) ?? ""}
                  onChange={(event) => set(key, event.target.value)}
                />
              </label>
            ))}

            <label className="block text-sm font-medium">
              {t("whatsapp")}
              <input
                placeholder={t("whatsappPlaceholder")}
                className="mt-2 h-11 w-full rounded-md border bg-background px-3"
                inputMode="tel"
                value={(form.whatsapp as string) ?? ""}
                onChange={(event) => set("whatsapp", maskPhone(event.target.value))}
              />
            </label>

            <section
              aria-labelledby="contest-title"
              className="overflow-hidden rounded-xl border border-primary/20 bg-gradient-to-br from-primary/10 via-primary/[0.04] to-transparent shadow-sm"
            >
              <div className="flex items-center gap-3  px-5 pt-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                  <Trophy className="h-5 w-5" aria-hidden="true" />
                </span>
                <p className="text-xl font-bold text-primary" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {t("contestPrize")}
                </p>
              </div>

              <div className="p-5">
                <p id="contest-title" className="text-balance text-lg font-semibold leading-snug text-foreground">
                  {t("contestTitle")}
                </p>

                <Textarea
                  aria-label={t("contestTitle")}
                  placeholder={t("contestPlaceholder")}
                  className="mt-4 min-h-28 resize-y bg-background text-base focus-visible:ring-primary"
                  value={(form.needs as string) ?? ""}
                  onChange={(event) => set("needs", event.target.value)}
                />

                <div className="mt-4 rounded-lg border border-primary/10 bg-orange-100/10 p-3">
                  <p className="text-xs leading-relaxed text-color-medium">{t("contestCriteria")}</p>
                  <p className="mt-2 text-sm font-semibold leading-relaxed text-primary">
                    {t("contestAnnouncement")}
                  </p>
                </div>

                <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground/70">
                  {t("contestPermission")}
                </p>
              </div>
            </section>

            <fieldset>
              <legend className="mb-2 text-sm font-medium">{t("demoInterest")}</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                {([[true, "yesDemo"], [false, "noDemo"]] as const).map(([value, label]) => (
                  <label
                    key={label}
                    className={`cursor-pointer rounded-lg border p-3 transition-colors text-sm ${
                      form.interestedInDemo === value ? "border-primary bg-primary/10" : "hover:bg-muted/50"
                    }`}
                  >
                    <input
                      className="mr-2"
                      type="radio"
                      name="interest"
                      checked={form.interestedInDemo === value}
                      onChange={() => set("interestedInDemo", value)}
                    />
                    {t(label)}
                  </label>
                ))}
              </div>
            </fieldset>

            <button
              type="submit"
              disabled={status === "sending"}
              className="h-11 w-full rounded-md bg-primary px-4 font-medium text-primary-foreground"
            >
              {status === "sending" ? t("sending") : t("send")}
            </button>

            {status === "success" && <p role="status" className="text-sm text-primary">{t("success")}</p>}
            {status === "error" && <p role="alert" className="text-sm text-destructive">{t("error")}</p>}
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
